import React, { useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { LifeBuoy, Send, Loader2, CheckCircle2, Info } from 'lucide-react';
import { Container } from '../components/ui';
import Reveal from '../components/Reveal';
import { createSupportTicket } from '../lib/marketingApi';
import FaqSearch from '../components/FaqSearch';
import CaptchaWidget from '../components/CaptchaWidget';

const CATEGORIES = [
  { value: 'support', label: 'Product support', hint: 'Help using the platform or a technical issue' },
  { value: 'billing', label: 'Billing & credits', hint: 'Invoices, credits, plans and payments' },
  { value: 'general', label: 'General enquiry', hint: 'Anything else' },
];

export default function Support() {
  const [params] = useSearchParams();
  const initial = CATEGORIES.some((c) => c.value === params.get('type')) ? params.get('type') : 'support';
  const [category, setCategory] = useState(initial);
  const [form, setForm] = useState({ name: '', email: '', company: '', org_id: '', subject: '', message: '', website: '' });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [capEnabled, setCapEnabled] = useState(false);
  const capRef = useRef(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.includes('@') || !form.message.trim()) {
      toast.error('Please add your name, a valid email and a message.');
      return;
    }
    let capToken = '';
    if (capEnabled) {
      capToken = await capRef.current?.getToken();
      if (!capToken) {
        toast.error('Anti-spam check failed. Please try again.');
        return;
      }
    }
    setLoading(true);
    try {
      await createSupportTicket({ ...form, category, captcha_token: capToken, page: window.location.pathname });
      setDone(true);
      toast.success('Thanks — your request has reached our support team.');
    } catch (err) {
      const msg = err?.response?.status === 429 ? 'Too many requests — please try again in a few minutes.'
        : err?.response?.data?.detail || 'Something went wrong. Please try again.';
      toast.error(msg);
      capRef.current?.reset();
    } finally {
      setLoading(false);
    }
  };

  const inp = 'h-11 w-full rounded-lg border border-slate-300 bg-white px-3.5 text-sm text-slate-900 outline-none transition-colors placeholder:text-slate-400 focus:border-brand dark:border-white/15 dark:bg-white/[0.04] dark:text-white';

  return (
    <section className="relative overflow-hidden px-5 py-16 sm:px-8 sm:py-20" data-testid="support-page">
      <div className="bp-grid-bg pointer-events-none absolute inset-0 opacity-30" />
      <Container className="relative">
        <div className="grid grid-cols-1 items-start gap-12 lg:grid-cols-2">
          <Reveal>
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-brand"><LifeBuoy className="h-4 w-4" /> Support</span>
            <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight text-slate-900 dark:text-white sm:text-4xl">How can we help?</h1>
            <p className="mt-4 max-w-lg text-base leading-relaxed text-slate-600 dark:text-slate-300">
              Send us a message and the right person on our team will reply from <span className="font-semibold text-slate-800 dark:text-white">support@billportal.io</span>, usually within one business day.
            </p>
            <div className="mt-8 rounded-2xl border border-slate-200 bg-white/70 p-5 dark:border-white/10 dark:bg-white/[0.03]">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-white"><Info className="h-4 w-4 text-brand" /> Already have an account?</div>
              <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-slate-400">
                Adding your <span className="font-medium">Organisation ID</span> helps us find your account faster. You’ll find it in <span className="font-medium">Org Settings</span> once you sign in — it’s optional.
              </p>
            </div>
          </Reveal>

          <Reveal delay={80}>
            {done ? (
              <div data-testid="support-success" className="rounded-2xl border border-emerald-200 bg-emerald-50 p-8 text-center dark:border-emerald-500/20 dark:bg-emerald-500/10">
                <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-500" />
                <h2 className="mt-4 text-xl font-bold text-slate-900 dark:text-white">Message received</h2>
                <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">Thanks, {form.name.split(' ')[0]}. We’ve logged your request and will reply to {form.email} shortly.</p>
                <button onClick={() => { setDone(false); setForm({ name: '', email: '', company: '', org_id: '', subject: '', message: '', website: '' }); }}
                  data-testid="support-new-btn" className="mt-6 rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand-600">Send another</button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="support-form" className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-white/[0.02] sm:p-8">
                <div className="mb-4">
                  <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">What’s this about?</label>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                    {CATEGORIES.map((c) => (
                      <button type="button" key={c.value} onClick={() => setCategory(c.value)} data-testid={`support-cat-${c.value}`}
                        className={`rounded-lg border px-3 py-2 text-left text-xs transition-colors ${category === c.value ? 'border-sky-500 bg-sky-50 text-sky-700 dark:bg-sky-500/10 dark:text-sky-300' : 'border-slate-200 text-slate-600 hover:border-slate-300 dark:border-white/10 dark:text-slate-300'}`}>
                        <div className="font-semibold">{c.label}</div>
                        <div className="mt-0.5 text-[11px] opacity-70">{c.hint}</div>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <input value={form.name} onChange={set('name')} placeholder="Your name *" data-testid="support-name" className={inp} />
                  <input value={form.email} onChange={set('email')} type="email" placeholder="Work email *" data-testid="support-email" className={inp} />
                </div>
                <input value={form.company} onChange={set('company')} placeholder="Company" data-testid="support-company" className={`mt-3 ${inp}`} />
                <div className="mt-3 flex items-center gap-1.5">
                  <input value={form.org_id} onChange={set('org_id')} placeholder="Organisation ID (optional)" data-testid="support-org-id" className={inp} />
                  <span data-testid="support-org-id-tip" title="Your Organisation ID — find it in Org Settings after you sign in. Optional, but it helps us locate your account faster."
                    className="flex h-7 w-7 shrink-0 cursor-help items-center justify-center rounded-full bg-slate-100 text-slate-400 hover:text-brand dark:bg-white/5">
                    <Info className="h-4 w-4" />
                  </span>
                </div>
                <input value={form.subject} onChange={set('subject')} placeholder="Subject" data-testid="support-subject" className={`mt-3 ${inp}`} />
                <textarea value={form.message} onChange={set('message')} rows={5} placeholder="How can we help? *" data-testid="support-message"
                  className="mt-3 w-full rounded-lg border border-slate-300 bg-white px-3.5 py-3 text-sm text-slate-900 outline-none transition-colors placeholder:text-slate-400 focus:border-brand dark:border-white/15 dark:bg-white/[0.04] dark:text-white" />
                {/* honeypot — hidden from humans */}
                <input value={form.website} onChange={set('website')} tabIndex={-1} autoComplete="off" aria-hidden="true"
                  className="absolute left-[-9999px] h-0 w-0 opacity-0" />
                <CaptchaWidget ref={capRef} onEnabledChange={setCapEnabled} className="mt-4" />
                <button type="submit" disabled={loading} data-testid="support-submit"
                  className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-brand-600 disabled:opacity-60">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send message
                </button>
                <p className="mt-3 text-center text-[11px] text-slate-400">Protected against spam. We’ll only use your details to respond to this request.</p>
              </form>
            )}
          </Reveal>
        </div>

        <div className="mt-16 border-t border-slate-200 pt-14 dark:border-white/10" data-testid="support-faq">
          <Reveal>
            <div className="mx-auto max-w-2xl text-center">
              <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-brand">Help Centre</span>
              <h2 className="mt-3 font-display text-2xl font-extrabold text-slate-900 dark:text-white sm:text-3xl">Answers to common questions</h2>
              <p className="mt-3 text-sm leading-relaxed text-slate-600 dark:text-slate-300">Search our FAQs — you might find your answer instantly, no waiting required.</p>
            </div>
          </Reveal>
          <div className="mt-8"><FaqSearch /></div>
        </div>
      </Container>
    </section>
  );
}

